import { EntityUtils } from '@record-it-npm/synchro-client';
import React, {
  ComponentProps,
  FunctionComponent,
  useCallback,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExpandableContainerWithHeader,
  ExpandableContainerWithHeaderProps
} from 'record-it-components/misc/ExpandableContainerWithHeader/ExpandableContainerWithHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { DurationUtils } from 'record-it-utils/utils/DurationUtils/DurationUtils';
import { WorkTimeEntryUtils } from '../../../classes/entities/WorkTimeEntry/WorkTimeEntryUtils';
import { useTodaysWorkTimeEntries } from '../../../hooks/entity/workTimeEntry/useTodaysWorkTimeEntries/useTodaysWorkTimeEntries';
import { TodaysWorkTimeInfoDetailEntry } from '../TodaysWorkTimeInfoDetailEntry/TodaysWorkTimeInfoDetailEntry';
import styles from './TodaysWorkTimeInfo.module.css';

export type TodaysWorkTimeInfoProps = PropsWithoutChildren<
  ComponentProps<'div'>
>;

export const TodaysWorkTimeInfo: FunctionComponent<TodaysWorkTimeInfoProps> =
  React.memo((props) => {
    const { ...remainingProps } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const workTimeEntries = useTodaysWorkTimeEntries();

    const formattedWorkTime = useMemo(() => {
      return DurationUtils.formatDurationToHourMinuteString(
        WorkTimeEntryUtils.getWorkTimeEntriesDurationSum(workTimeEntries)
      );
    }, [workTimeEntries]);

    const renderHeader: ExpandableContainerWithHeaderProps['renderHeader'] =
      useCallback(
        ({ expanded }) => {
          return t('components.workTime.TodaysWorkTimeInfo.moreInfosTitle', {
            count: workTimeEntries.length,
            context: expanded ? 'expanded' : ''
          });
        },
        [t, workTimeEntries.length]
      );

    return (
      <div {...remainingProps}>
        <div>
          <span>{t('components.workTime.TodaysWorkTimeInfo.workTime')}</span>
          <span data-testid="TodaysWorkTimeInfo.WorkTime">
            {formattedWorkTime}
          </span>
        </div>
        {workTimeEntries.length > 0 ? (
          <ExpandableContainerWithHeader
            renderHeader={renderHeader}
            className={styles.detailContainer}
            data-testid="TodaysWorkTimeInfo.DetailsExpandableContainer"
          >
            <div className={styles.detailEntriesList}>
              {workTimeEntries.map((workTimeEntry) => (
                <TodaysWorkTimeInfoDetailEntry
                  key={EntityUtils.getTrackingKey(workTimeEntry)}
                  workTimeEntry={workTimeEntry}
                  data-testid="TodaysWorkTimeInfo.DetailEntry"
                ></TodaysWorkTimeInfoDetailEntry>
              ))}
            </div>
          </ExpandableContainerWithHeader>
        ) : null}
      </div>
    );
  });
